/**
 * GlovesPage — Sterile Mate Disposable Gloves marketplace (PUBLIC)
 * Route: /marketplace/gloves
 *
 * IMPORTANT: This page is customer-facing. It shows RETAIL pricing only.
 * Sterile Mate's AMMEX cost prices, margins, or markup are NEVER rendered
 * here — cost data is exposed only through the admin-only route
 * /admin/gloves-pricing behind role super_admin / hub_admin.
 *
 * Data source: /app/frontend/src/data/glovesCatalog.js (27 product lines,
 * 100+ SKUs). The frontend catalog itself contains no cost figures.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";
import GloveBrandTabs from "@/components/GloveBrandTabs";
import { Mail, Phone, ShieldCheck, Package, ExternalLink, ShoppingCart, FileText, Info, X } from "lucide-react";
import { GLOVES, GLOVE_MATERIALS, GLOVES_SKU_COUNT, UOM_LABELS } from "@/data/glovesCatalog";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import { toast } from "sonner";

const MATERIAL_BADGE = {
  nitrile: "bg-blue-100 text-blue-800 border-blue-200",
  latex: "bg-amber-100 text-amber-800 border-amber-200",
  vinyl: "bg-emerald-100 text-emerald-800 border-emerald-200",
  poly: "bg-slate-100 text-slate-700 border-slate-200",
};

const fmt = (n) => `$${n.toFixed(2)}`;
const fmtPiece = (n) => `$${n.toFixed(4)}`;

export default function GlovesPage() {
  const [filter, setFilter] = useState("all");
  const [detailProduct, setDetailProduct] = useState(null);
  const [nitrileMil, setNitrileMil] = useState("4mil");
  // Per-product selected size: { [productId]: sizeString }. Defaults to the
  // first available variant on first render (see below).
  const [selectedSize, setSelectedSize] = useState({});
  const { addItem } = useQuoteCart();

  // Pick the matching variant (or fall back to the first) for a product.
  const variantFor = (p) => {
    const size = selectedSize[p.id] || p.variants[0]?.size;
    return p.variants.find((v) => v.size === size) || p.variants[0];
  };

  const filtered = useMemo(
    () => (filter === "all" ? GLOVES : GLOVES.filter((g) => g.material === filter)),
    [filter]
  );

  const counts = useMemo(() => {
    const c = { all: GLOVES.length };
    for (const m of GLOVE_MATERIALS) {
      if (m.key === "all") continue;
      c[m.key] = GLOVES.filter((g) => g.material === m.key).length;
    }
    return c;
  }, []);

  const handleAdd = (product) => {
    const v = variantFor(product);
    addItem(
      {
        erp: v?.sku || product.id.toUpperCase(),
        name: `${product.name} — Size ${v?.size || "N/A"}`,
        image: product.image,
        source: `Sterile Mate — ${product.brand}`,
        pack: `${product.packDescription} · UOM ${product.uom || "CA"}`,
        price: product.retailPerCase,
      },
      1
    );
    toast.success(`Added: ${product.name} · Size ${v?.size} (${v?.sku})`);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="gloves-page">
      <SiteHeader />
      <MarketplaceCategoryNav currentCategory="gloves" />
      <MarketplaceBreadcrumb
        trail={[{ label: "Marketplace", to: "/marketplace" }, { label: "Disposable Gloves" }]}
      />

      {/* Sterile Mate Own-Brand Nitrile Exam Glove — Coming Soon launch hero */}
      <section
        data-testid="sterilemate-nitrile-launch-hero"
        className="border-b border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-slate-900 font-bold text-[11px] uppercase tracking-widest px-3 py-1.5 mb-4">
                <ShieldCheck className="w-3.5 h-3.5" />
                New Sterile Mate Own-Brand · Coming Q2 2026
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-3">
                Sterile Mate<sup>®</sup> Blue Exam Nitrile
                <span className="block text-cyan-300 text-2xl md:text-3xl mt-1">
                  3.5 mil &amp; 4 mil · Powder-Free · Chemo &amp; Fentanyl Tested
                </span>
              </h1>
              <p className="text-base text-white/85 mb-5 leading-relaxed max-w-2xl">
                Sterile Mate&apos;s own-brand nitrile examination glove — single-use, non-sterile,
                fingertip-textured, ambidextrous, powder-free. Tested for chemotherapy drug
                permeation (ASTM D6978) and fentanyl exposure. Not made with natural rubber
                latex. Launching in 100 ct dispensing boxes, four sizes: S · M · L · XL.
              </p>

              {/* Mil selector */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-bold uppercase tracking-widest text-white/60">Thickness:</span>
                {[
                  { key: "3.5mil", label: "3.5 mil" },
                  { key: "4mil",   label: "4 mil" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setNitrileMil(m.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${nitrileMil === m.key
                      ? "bg-cyan-400 text-slate-900 shadow-lg"
                      : "bg-white/10 hover:bg-white/20 text-white border border-white/20"}`}
                    data-testid={`nitrile-mil-${m.key}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {/* Size chip row */}
              <div className="flex items-center gap-2 flex-wrap mb-6">
                <span className="text-xs font-bold uppercase tracking-widest text-white/60 mr-1">Sizes:</span>
                {["S", "M", "L", "XL"].map((s) => (
                  <span key={s} className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-white/15 border border-white/25 text-white font-bold text-sm">
                    {s}
                  </span>
                ))}
                <span className="text-xs text-white/60 ml-2">· 100 pcs / box</span>
              </div>

              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-white/90 mb-6">
                {[
                  "Powder-Free",
                  "Non-Sterile",
                  "Fingertip Textured",
                  "Ambidextrous",
                  "Examination Grade",
                  "Chemo Tested (ASTM D6978)",
                  "Fentanyl Exposure Tested",
                  "Not Made With Natural Rubber Latex",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="mailto:orders@sterilemate.com?subject=Sterile%20Mate%20Nitrile%20Exam%20Gloves%20-%20Reserve%20Stock&body=I'd%20like%20to%20reserve%20stock%20of%20the%20Sterile%20Mate%20nitrile%20exam%20gloves%20(3.5mil%20/%204mil)%20for%20launch."
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-5 py-3 shadow-lg shadow-amber-500/25 transition-colors"
                  data-testid="sterilemate-nitrile-cta-reserve"
                >
                  <Mail className="w-4 h-4" />
                  Reserve Stock &amp; Notify Me at Launch
                </a>
                <a
                  href="/api/static/gloves/sterilemate_nitrile_exam_dieline_4mil.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/25 text-white font-bold px-5 py-3 transition-colors"
                  data-testid="sterilemate-nitrile-cta-dieline"
                >
                  <FileText className="w-4 h-4" />
                  Download Dieline Artwork (PDF)
                </a>
              </div>
              <p className="text-xs text-white/60 mt-3">
                Manufacturer artwork · dimensions · cut &amp; fold specs — 4 mil template shown, 3.5 mil variant follows the same die
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-2xl bg-white">
              <img
                src="/api/static/gloves/sterilemate_nitrile_exam_hero.png"
                alt="Sterile Mate Blue Exam Nitrile Gloves — 3.5 mil and 4 mil dispensing boxes"
                className="w-full h-auto"
                data-testid="sterilemate-nitrile-hero-img"
              />
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 text-center text-sm text-white/70">
            <span className="uppercase tracking-widest text-[11px] font-bold text-cyan-300">
              Shop Our Current Disposable Glove Catalog
            </span>
            <span className="mx-3 text-white/30">·</span>
            <span>AMMEX Professional · Gloveworks · X3 — {GLOVES.length} product lines below</span>
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-white to-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[3px] text-blue-700 mb-3">
                Sterile Mate · AMMEX Authorized Distributor · Disposable Gloves
              </p>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-[1.05] tracking-tight mb-4"
                data-testid="gloves-hero-title"
              >
                Every glove your team wears —<br />
                <span className="text-blue-600">on one PO.</span>
              </h1>
              <p className="text-lg text-slate-700 leading-relaxed mb-6 max-w-2xl">
                {GLOVES.length} product lines · {GLOVES_SKU_COUNT} SKUs — Nitrile, Latex, Vinyl, and Poly across
                AMMEX Professional (exam-grade), Gloveworks (industrial &amp; RDT), and X3 (value).
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> FDA 510(k) exam-grade lines
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
                  <Package className="w-3.5 h-3.5 text-blue-600" /> Case-quantity pricing
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
                  Chemo · Fentanyl tested options
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <a
                  href="mailto:orders@sterilemate.com?subject=Disposable%20Gloves%20-%20Quote%20Request"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-black text-sm transition-all shadow-sm"
                  data-testid="gloves-hero-quote-cta"
                >
                  <Mail className="w-4 h-4" /> Request a full-catalog quote
                </a>
                <a
                  href="tel:8009883892"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 hover:border-blue-600 bg-white text-slate-900 text-sm font-bold transition-all"
                  data-testid="gloves-hero-phone"
                >
                  <Phone className="w-4 h-4" /> 800.988.3892
                </a>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-sm bg-white">
              <img
                src="/api/static/gloves/ammex-blue-nitrile-exam.png"
                alt="AMMEX Gloveworks nitrile exam gloves"
                className="w-full h-64 lg:h-96 object-contain p-6 bg-slate-50"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MATERIAL FILTER STRIP */}
      <section className="border-b border-slate-200 bg-white sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <GloveBrandTabs />
          <div className="w-px h-4 bg-slate-200 mx-1" />
          <span className="text-[10px] font-black uppercase tracking-[1.5px] text-slate-500 shrink-0 mr-1">
            Filter:
          </span>
          {GLOVE_MATERIALS.map((m) => {
            const active = filter === m.key;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => setFilter(m.key)}
                data-testid={`gloves-filter-${m.key}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap transition-colors border ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                }`}
              >
                {m.label}
                <span
                  className={`inline-flex items-center justify-center min-w-[22px] h-4 px-1 rounded text-[10px] font-black ${
                    active ? "bg-white text-slate-900" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {counts[m.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>
        {/* UOM legend */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-3 pt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500">
          <span className="font-black uppercase tracking-[1.5px] text-slate-400">UOM Legend:</span>
          {Object.values(UOM_LABELS).map((u) => (
            <span key={u.code} data-testid={`uom-legend-${u.code}`} className="inline-flex items-center gap-1">
              <span className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[9px] font-black">{u.code}</span>
              <span className="text-slate-600 font-semibold">{u.label}</span>
              <span className="text-slate-400">— {u.desc}</span>
            </span>
          ))}
        </div>
      </section>

      {/* PRODUCTS GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-2">
          <h2 className="text-2xl font-black text-slate-900">
            All Disposable Gloves{" "}
            <span className="text-slate-500 font-medium">· {filtered.length} product lines</span>
          </h2>
          <p className="text-xs text-slate-500">
            Case pricing includes standard freight terms · Sterile Mate LLC · Certified Small Business
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:ring-2 hover:ring-blue-500/40 hover:shadow-lg transition-all flex flex-col"
              data-testid={`glove-product-${p.id}`}
            >
              <div className="aspect-square bg-slate-50 relative">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-contain p-3"
                />
                <span
                  className={`absolute top-3 left-3 px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider border ${
                    MATERIAL_BADGE[p.material] || MATERIAL_BADGE.poly
                  }`}
                >
                  {p.material} · {p.mil} mil
                </span>
                <span className="absolute top-3 right-3 px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider bg-slate-900/80 text-white">
                  {p.variants.length} sizes
                </span>
              </div>
              <div className="p-4 flex flex-col flex-1">
                <p className="text-[10px] font-black uppercase tracking-[2px] text-blue-700 mb-1">
                  {p.brand}
                </p>
                <h3 className="text-sm font-bold text-slate-900 leading-snug mb-1.5 min-h-[42px]">
                  {p.name}
                </h3>
                <p className="text-[11px] text-slate-500 mb-3">{p.packDescription}</p>

                {/* RETAIL PRICING (customer-facing only) */}
                <div className="rounded-lg border border-slate-200 p-3 mb-3 bg-slate-50">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[1.5px] text-blue-700">
                        Price · per {UOM_LABELS[p.uom || "CA"]?.label.toLowerCase() || "case"}
                        <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded bg-blue-100 text-blue-700 text-[9px] font-black" title={UOM_LABELS[p.uom || "CA"]?.desc}>
                          UOM: {p.uom || "CA"}
                        </span>
                      </p>
                      <p className="text-xl font-black text-slate-900" data-testid={`glove-retail-${p.id}`}>
                        {fmt(p.retailPerCase)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-semibold text-slate-500">per piece</p>
                      <p className="text-sm font-bold text-slate-700">{fmtPiece(p.retailPerPiece)}</p>
                    </div>
                  </div>
                </div>

                {/* SIZE SELECTOR — click to pick, drives the SKU added to cart */}
                <div className="mb-3">
                  <p className="text-[10px] font-black uppercase tracking-[1.5px] text-slate-500 mb-1.5">
                    Select size
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.variants.map((v) => {
                      const active = (selectedSize[p.id] || p.variants[0]?.size) === v.size;
                      return (
                        <button
                          key={v.sku}
                          type="button"
                          onClick={() => setSelectedSize((s) => ({ ...s, [p.id]: v.size }))}
                          title={v.sku}
                          data-testid={`glove-size-${p.id}-${v.size}`}
                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-black border transition-colors ${
                            active
                              ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                              : "bg-white text-slate-700 border-slate-300 hover:border-blue-500 hover:text-blue-700"
                          }`}
                        >
                          {v.size}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1.5 font-mono">
                    SKU: <span className="text-slate-700 font-bold">{variantFor(p)?.sku}</span>
                  </p>
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3 mt-auto">
                  <button
                    type="button"
                    onClick={() => handleAdd(p)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-black text-xs transition-all"
                    data-testid={`glove-add-${p.id}`}
                  >
                    <ShoppingCart className="w-3.5 h-3.5" /> Add to Quote
                  </button>
                  <button
                    type="button"
                    onClick={() => setDetailProduct(p)}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 hover:text-blue-700"
                    data-testid={`glove-details-${p.id}`}
                  >
                    <Info className="w-3 h-3" /> Specs
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA STRIP */}
        <div className="mt-14 bg-gradient-to-r from-blue-50 to-slate-50 rounded-2xl border border-blue-200 p-8 lg:p-10 text-center">
          <h3 className="text-2xl lg:text-3xl font-black text-slate-900 mb-3">
            Need a private-label or bulk pallet program?
          </h3>
          <p className="text-slate-700 max-w-2xl mx-auto mb-6">
            Sterile Mate is an authorized AMMEX distributor. Ask us about palletized
            case-rate pricing, custom color runs, and hospital contract terms.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="mailto:orders@sterilemate.com?subject=Gloves%20-%20Bulk%20Program"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-black text-sm transition-all shadow-sm"
              data-testid="gloves-bottom-quote"
            >
              <Mail className="w-4 h-4" /> Email a custom list
            </a>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 hover:border-blue-600 bg-white text-slate-900 text-sm font-bold transition-all"
              data-testid="gloves-bottom-marketplace"
            >
              Back to Marketplace
            </Link>
          </div>
        </div>
      </section>

      {/* SPEC-SHEET DETAIL MODAL (retail only) */}
      {detailProduct && (
        <SpecModal product={detailProduct} onClose={() => setDetailProduct(null)} />
      )}
    </div>
  );
}

function SpecModal({ product, onClose }) {
  const p = product;
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="glove-spec-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[2px] text-blue-700">{p.brand}</p>
            <h2 className="text-lg lg:text-xl font-black text-slate-900 leading-tight">{p.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
            data-testid="glove-spec-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid md:grid-cols-[220px_1fr] gap-6">
          {/* Image + price */}
          <div>
            <div className="aspect-square bg-slate-50 rounded-xl border border-slate-200 overflow-hidden mb-4">
              <img src={p.image} alt={p.name} className="w-full h-full object-contain p-3" />
            </div>
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
              <p className="text-[10px] font-black uppercase tracking-[1.5px] text-blue-700 mb-1">Price · per case</p>
              <p className="text-2xl font-black text-slate-900">{fmt(p.retailPerCase)}</p>
              <p className="text-xs text-slate-600 mt-0.5">{fmtPiece(p.retailPerPiece)} / glove</p>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-5">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-500 mb-1">Use Case</p>
              <p className="text-sm text-slate-800">{p.useCase}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <SpecRow label="Material" value={`${p.material.toUpperCase()} · ${p.color || "—"}`} />
              <SpecRow label="Thickness" value={`${p.mil} mil`} />
              <SpecRow label="Pack" value={p.packDescription} />
              <SpecRow label="Gloves / case" value={String(p.caseCount)} />
            </div>

            <div>
              <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-500 mb-2">Features</p>
              <ul className="text-sm text-slate-800 space-y-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-500 mb-2">Standards / Certifications</p>
              <div className="flex flex-wrap gap-1.5">
                {p.standards.map((s) => (
                  <span key={s} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-bold bg-blue-50 text-blue-800 border border-blue-200">
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-500 mb-2">Sizes &amp; SKUs</p>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-black text-slate-600 uppercase tracking-wider text-[10px]">Size</th>
                      <th className="text-left px-3 py-2 font-black text-slate-600 uppercase tracking-wider text-[10px]">SKU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.variants.map((v, i) => (
                      <tr key={v.sku} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="px-3 py-2 font-bold text-slate-900">{v.size}</td>
                        <td className="px-3 py-2 font-mono text-slate-700">{v.sku}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <a
                href={p.specSheet}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-xs font-black"
                data-testid="glove-spec-download"
              >
                <FileText className="w-3.5 h-3.5" /> Download spec sheet
              </a>
              <a
                href={p.productUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 hover:border-blue-600 bg-white text-slate-900 text-xs font-bold"
                data-testid="glove-spec-manufacturer"
              >
                Manufacturer page <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}
